/**
 * src/pages/admin/AdminModeration.js
 * 
 * Content Moderation panel for admins.
 */

import React, { useEffect, useState } from "react";
import {
  ShieldAlert,
  Package,
  Heart,
  Eye,
  EyeOff,
  Trash2,
  CheckCircle2,
  Calendar,
  User,
  AlertTriangle,
  Loader2,
  Image as ImageIcon,
  MessageSquare,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import * as adminService from "../../services/adminService";
import { useToast } from "../../contexts/ToastContext";
import ConfirmDialog from "../../components/ui/ConfirmDialog";
import { useAdmin } from "../../hooks/useAdmin";

function formatDate(ts) {
  if (!ts) return "—";
  const d = ts.seconds ? new Date(ts.seconds * 1000) : new Date(ts);
  return d.toLocaleDateString("en", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

const TABS = [
  { id: "products", label: "Products", Icon: Package },
];

// ─── Flagged Item Card ──────────────────────────────────────────
function FlaggedCard({ item, type, onAction, adminUser }) {
  const [expanded, setExpanded] = useState(false);
  const [reason, setReason] = useState("");
  const [actionLoading, setActionLoading] = useState("");
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    message: "",
    loading: false,
    action: null,
  });
  const toast = useToast();

  const handleAction = async (action) => {
    const actionLabel = action === "approve" ? "approve" : action === "hide" ? "hide" : "remove";
    setConfirmDialog({
      open: true,
      title: `Confirm ${actionLabel}`,
      message: `Are you sure you want to ${actionLabel} this product?`,
      loading: false,
      action: async () => {
        setActionLoading(action);
        try {
          await adminService.withAdminRetry(() => adminService.moderateProduct(adminUser, item.id, action, reason));
          toast.success(
            action === "approve" ? "Approved and unflagged" :
            action === "hide" ? "Hidden from public" :
            "Removed"
          );
          onAction?.();
        } finally {
          setActionLoading("");
        }
      },
    });
  };

  const runConfirmAction = async () => {
    if (!confirmDialog.action) return;
    setConfirmDialog((prev) => ({ ...prev, loading: true }));
    try {
      await confirmDialog.action();
      setConfirmDialog({ open: false, title: "", message: "", loading: false, action: null });
    } catch (err) {
      toast.error(`Failed: ${err?.message || "Action failed"}`);
      setConfirmDialog((prev) => ({ ...prev, loading: false }));
    }
  };

  return (
    <div className="bg-white dark:bg-neutral-800 rounded-xl border border-orange-200 dark:border-orange-900/50 overflow-hidden">
      {/* Header */}
      <div className="p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-neutral-750 transition" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-start gap-3">
          {/* Thumbnail or icon */}
          <div className="w-14 h-14 rounded-lg bg-gray-100 dark:bg-neutral-700 flex items-center justify-center flex-shrink-0 overflow-hidden">
            {item.images?.[0] || item.imageUrl ? (
              <img src={item.images?.[0] || item.imageUrl} alt="" className="w-14 h-14 object-cover rounded-lg" />
            ) : (
              <ImageIcon className="w-6 h-6 text-gray-400" />
            )}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-bold text-gray-900 dark:text-white truncate">
                {item.title || item.name || "Untitled"}
              </span>
              {item.flagReason && (
                <span className="px-1.5 py-0.5 rounded text-xs font-bold bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  Flagged
                </span>
              )}
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-1 mt-0.5">
              {item.description || "No description"}
            </p>
            <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
              <span className="flex items-center gap-1">
                <User className="w-3 h-3" />
                {item.userName || "Unknown"}
              </span>
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {formatDate(item.createdAt)}
              </span>
            </div>
          </div>

          <div className="flex-shrink-0">
            {expanded ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
          </div>
        </div>
      </div>

      {/* Expanded Details */}
      {expanded && (
        <div className="border-t border-gray-200 dark:border-neutral-700 p-4 space-y-3">
          {/* Description */}
          <div className="text-sm text-gray-700 dark:text-gray-300">
            {item.description || "No description provided."}
          </div>

          {/* Flag Reason */}
          {item.flagReason && (
            <div className="p-2.5 rounded-lg bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 text-sm">
              <span className="font-medium text-orange-700 dark:text-orange-400">Flag reason:</span>
              <span className="text-orange-600 dark:text-orange-300 ml-1">{item.flagReason}</span>
            </div>
          )}

          {/* Reports */}
          {item.reports && item.reports.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 flex items-center gap-1">
                <MessageSquare className="w-3.5 h-3.5" />
                Reports ({item.reports.length})
              </p>
              {item.reports.map((r, i) => (
                <div key={i} className="p-2 rounded bg-gray-50 dark:bg-neutral-900 text-xs text-gray-600 dark:text-gray-400">
                  {r.reason || r.text || JSON.stringify(r)}
                </div>
              ))}
            </div>
          )}

          {/* Images Gallery */}
          {(item.images?.length > 0 || item.imageUrl) && (
            <div className="flex gap-2 flex-wrap">
              {(item.images || [item.imageUrl]).filter(Boolean).map((src, idx) => (
                <a key={idx} href={src} target="_blank" rel="noopener noreferrer">
                  <img src={src} alt="" className="w-20 h-20 object-cover rounded-lg border border-gray-200 dark:border-neutral-700 hover:opacity-80 transition" />
                </a>
              ))}
            </div>
          )}

          {/* Moderation Reason */}
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Moderation note (optional)…"
            className="w-full px-3 py-2 border border-gray-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-900 text-sm focus:ring-2 focus:ring-green-500 outline-none"
          />

          {/* Actions */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => handleAction("approve")}
              disabled={!!actionLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-600 hover:bg-green-700 text-white text-xs font-medium transition disabled:opacity-50"
            >
              {actionLoading === "approve" ? <span className="orrith-loader orrith-loader-xs orrith-loader-primary" /> : <CheckCircle2 className="w-3 h-3" />}
              Approve
            </button>
            <button
              onClick={() => handleAction("hide")}
              disabled={!!actionLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 text-xs font-medium hover:bg-yellow-200 transition disabled:opacity-50"
            >
              {actionLoading === "hide" ? <span className="orrith-loader orrith-loader-xs orrith-loader-primary" /> : <EyeOff className="w-3 h-3" />}
              Hide
            </button>
            <button
              onClick={() => handleAction("remove")}
              disabled={!!actionLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-xs font-medium hover:bg-red-200 transition disabled:opacity-50"
            >
              {actionLoading === "remove" ? <span className="orrith-loader orrith-loader-xs orrith-loader-primary" /> : <Trash2 className="w-3 h-3" />}
              Remove
            </button>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        loading={confirmDialog.loading}
        destructive={true}
        confirmLabel="Confirm"
        onConfirm={runConfirmAction}
        onCancel={() => {
          if (confirmDialog.loading) return;
          setConfirmDialog({ open: false, title: "", message: "", loading: false, action: null });
        }}
      />
    </div>
  );
}

// ─── Main Page ──────────────────────────────────────────────────
export default function AdminModeration() {
  const { user: adminUser } = useAdmin();
  const [tab, setTab] = useState("products");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const loadItems = async () => {
    setLoading(true);
    try {
      const data = await adminService.fetchFlaggedProducts();
      setItems(data);
      setLoadError("");
    } catch (err) {
      setItems([]);
      setLoadError(err?.message || "Failed to load moderation items");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();

  }, [tab]);

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-white dark:bg-neutral-900 border-b border-gray-200 dark:border-neutral-800">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-3 h-14">
            <ShieldAlert className="w-5 h-5 text-orange-500" />
            <h1 className="text-lg font-bold text-gray-900 dark:text-white">
              Content Moderation
            </h1>
            <span className="text-xs text-gray-500 dark:text-gray-400 ml-auto">
              {items.length} flagged
            </span>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 pb-3 overflow-x-auto scrollbar-hide">
            {TABS.map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`
                  flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium border transition whitespace-nowrap
                  ${tab === t.id
                    ? 'bg-green-600 text-white border-green-600'
                    : 'border-gray-200 dark:border-neutral-700 text-gray-600 dark:text-gray-300 hover:border-green-500'
                  }
                `}
              >
                <t.Icon className="w-3.5 h-3.5" />
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-5 pb-24 space-y-3">
        {loadError && (
          <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-3 py-2 text-xs text-red-700 dark:text-red-300 flex items-center justify-between gap-3">
            <span>{loadError}</span>
            <button
              onClick={loadItems}
              className="px-2.5 py-1 rounded bg-red-600 hover:bg-red-700 text-white text-xs font-medium"
            >
              Retry
            </button>
          </div>
        )}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="orrith-loader orrith-loader-md orrith-loader-primary" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-16">
            <Eye className="w-12 h-12 text-gray-300 dark:text-neutral-600 mx-auto mb-3" />
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
              No flagged {tab}
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
              All clean! Check back later.
            </p>
          </div>
        ) : (
          items.map((item) => (
            <FlaggedCard
              key={item.id}
              item={item}
              type={tab}
              onAction={loadItems}
              adminUser={adminUser}
            />
          ))
        )}
      </div>
    </div>
  );
}
